import axios from 'axios';

const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';

let clearing = false;

/** Clear stored session (localStorage + axios default header). */
export function clearAuthSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  delete axios.defaults.headers.common['Authorization'];
}

/** Apply Bearer token to axios defaults. */
export function applyAuthToken(token: string) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  axios.defaults.withCredentials = true;
}

/**
 * True when 401 means our JWT/session is invalid — NOT a proxied Jira OAuth failure.
 * Ticket/sprint endpoints used to forward Jira 401 and that logged users out mid-click.
 */
function isAppSessionUnauthorized(error: any): boolean {
  const detail = error?.response?.data?.detail;
  const detailStr =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).join(' ')
        : detail != null
          ? JSON.stringify(detail)
          : '';

  // Known JWT / FastAPI security messages
  if (/could not validate credentials|not authenticated|invalid token|token expired|signature/i.test(detailStr)) {
    return true;
  }
  // Explicitly NOT app auth: Jira/OAuth/integration problems
  if (/jira|atlassian|oauth|sprint|backlog|worklog|issue/i.test(detailStr)) {
    return false;
  }
  // WWW-Authenticate from HTTPBearer without a Jira-ish body → treat as session
  const www = error?.response?.headers?.['www-authenticate'];
  if (www && !detailStr) return true;

  // Default: do not nuke the session on ambiguous 401s from feature APIs
  const url = String(error?.config?.url || '');
  if (url.includes('/api/auth/')) return true;
  return false;
}

/**
 * On app-session 401 (except login), drop the stale session and reload to Login.
 * Does NOT treat Jira OAuth 401s (sprint move, tickets, …) as logout.
 */
export function installAuthAxiosInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = String(error?.config?.url || '');
      const isLogin = url.includes('/api/auth/login');
      if (
        status === 401 &&
        !isLogin &&
        localStorage.getItem(TOKEN_KEY) &&
        !clearing &&
        isAppSessionUnauthorized(error)
      ) {
        clearing = true;
        clearAuthSession();
        // Full reload so App remounts on Login without half-initialized state
        window.location.reload();
      }
      return Promise.reject(error);
    }
  );
}
